import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AICanvasEditor
 *
 * OpenAI Canvas-style live editing effect for content analysis.
 *
 * EFFECTS REPLICATED FROM CANVAS:
 * 1. Inline line backgrounds (not boxes) - full-width colored backgrounds
 * 2. Subtle status bullets in the left margin (●)
 * 3. Organic fade transitions between states
 * 4. Text changes appear with typewriter/fade effect
 * 5. Additions glow green, removals fade to red before disappearing
 * 6. Sidebar with edit history and explanations
 *
 * Uses Framer Motion for smooth organic transitions.
 */
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
// ============================================
// LINE STATUS COLORS (inline, no boxes)
// ============================================
const STATUS_STYLES = {
    idle: {
        bg: 'transparent',
        text: 'text-slate-800 dark:text-slate-200',
        bullet: 'bg-slate-300 dark:bg-slate-600',
        bulletOpacity: 0.3,
    },
    scanning: {
        bg: 'rgba(251, 191, 36, 0.08)', // Very subtle amber
        text: 'text-slate-800 dark:text-slate-200',
        bullet: 'bg-amber-400',
        bulletOpacity: 1,
    },
    analyzing: {
        bg: 'rgba(251, 191, 36, 0.15)', // Slightly more visible amber
        text: 'text-slate-900 dark:text-slate-100',
        bullet: 'bg-amber-500',
        bulletOpacity: 1,
    },
    adding: {
        bg: 'rgba(34, 197, 94, 0.15)', // Soft green
        text: 'text-emerald-900 dark:text-emerald-100',
        bullet: 'bg-emerald-500',
        bulletOpacity: 1,
    },
    removing: {
        bg: 'rgba(239, 68, 68, 0.12)', // Soft red
        text: 'text-red-700 dark:text-red-300 line-through opacity-60',
        bullet: 'bg-red-500',
        bulletOpacity: 1,
    },
    modified: {
        bg: 'rgba(59, 130, 246, 0.12)', // Soft blue
        text: 'text-blue-900 dark:text-blue-100',
        bullet: 'bg-blue-500',
        bulletOpacity: 1,
    },
    complete: {
        bg: 'rgba(34, 197, 94, 0.08)', // Very subtle green
        text: 'text-slate-800 dark:text-slate-200',
        bullet: 'bg-emerald-400',
        bulletOpacity: 0.8,
    },
};
// ============================================
// ANIMATED LINE COMPONENT
// ============================================
const AnimatedLine = React.memo(({ line, isActive, }) => {
    const status = line.status || 'idle';
    const styles = STATUS_STYLES[status];
    // Render content based on type
    const renderContent = () => {
        switch (line.type) {
            case 'heading':
                const HeadingTag = `h${line.level || 2}`;
                const headingSizes = {
                    1: 'text-2xl font-bold',
                    2: 'text-xl font-semibold',
                    3: 'text-lg font-medium',
                };
                return (_jsx(HeadingTag, { className: headingSizes[line.level || 2], children: line.content }));
            case 'list-item':
                return (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: "text-slate-400 select-none", children: "\u2022" }), _jsx("span", { children: line.content })] }));
            case 'quote':
                return (_jsx("blockquote", { className: "border-l-4 border-slate-300 dark:border-slate-600 pl-4 italic text-slate-600 dark:text-slate-400", children: line.content }));
            case 'code':
                return (_jsx("code", { className: "font-mono text-sm bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded", children: line.content }));
            case 'divider':
                return _jsx("hr", { className: "border-slate-200 dark:border-slate-700 my-2" });
            default:
                return _jsx("p", { className: "leading-relaxed", children: line.content });
        }
    };
    return (_jsxs(motion.div, { layout: true, className: "relative group", initial: { opacity: 0, y: 5 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, height: 0, marginBottom: 0 }, transition: { duration: 0.2, ease: 'easeOut' }, children: [_jsx(motion.div, { className: "absolute -inset-x-4 inset-y-0 -z-10 rounded-sm", initial: false, animate: {
                    backgroundColor: styles.bg,
                    opacity: status === 'idle' ? 0 : 1,
                }, transition: { duration: 0.3, ease: 'easeInOut' } }), _jsx(motion.div, { className: `absolute -left-6 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${styles.bullet}`, initial: { scale: 0, opacity: 0 }, animate: {
                    scale: status !== 'idle' ? 1 : 0.5,
                    opacity: styles.bulletOpacity,
                }, transition: { duration: 0.2 } }), _jsx(AnimatePresence, { children: (status === 'scanning' || status === 'analyzing') && (_jsx(motion.div, { className: "absolute -inset-x-4 inset-y-0 -z-20", initial: { opacity: 0 }, animate: {
                        opacity: [0.3, 0.6, 0.3],
                        backgroundColor: status === 'analyzing'
                            ? 'rgba(251, 191, 36, 0.1)'
                            : 'rgba(251, 191, 36, 0.05)',
                    }, exit: { opacity: 0 }, transition: {
                        opacity: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
                        backgroundColor: { duration: 0.3 },
                    } })) }), _jsx(motion.div, { className: `relative ${styles.text} transition-colors duration-300`, animate: {
                    filter: status === 'removing' ? 'blur(0.5px)' : 'blur(0px)',
                }, children: renderContent() }), _jsx(motion.div, { className: "absolute left-0 top-0 bottom-0 w-0.5 bg-primary-500", initial: { scaleY: 0, opacity: 0 }, animate: {
                    scaleY: isActive ? 1 : 0,
                    opacity: isActive ? 1 : 0,
                }, transition: { duration: 0.2 }, style: { originY: 0 } })] }));
});
AnimatedLine.displayName = 'AnimatedLine';
// ============================================
// EDIT HISTORY SIDEBAR
// ============================================
const EditHistorySidebar = ({ history, isVisible, }) => {
    if (!isVisible)
        return null;
    const typeIcons = {
        add: '✅',
        remove: '❌',
        modify: '✏️',
        analyze: '🔍',
    };
    const typeColors = {
        add: 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950',
        remove: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
        modify: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950',
        analyze: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950',
    };
    return (_jsx(motion.aside, { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -20 }, className: "w-72 border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 overflow-y-auto", children: _jsxs("div", { className: "p-4", children: [_jsx("h3", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3", children: "Historial de ediciones" }), _jsxs("div", { className: "space-y-2", children: [_jsx(AnimatePresence, { mode: "popLayout", children: history.slice().reverse().map((item) => (_jsx(motion.div, { initial: { opacity: 0, y: -10, scale: 0.95 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, scale: 0.95 }, className: `p-3 rounded-lg border text-sm ${typeColors[item.type]}`, children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: "text-base", children: typeIcons[item.type] }), _jsx("p", { className: "text-slate-700 dark:text-slate-300 leading-snug", children: item.description })] }) }, item.id))) }), history.length === 0 && (_jsx("p", { className: "text-sm text-slate-500 dark:text-slate-400 italic", children: "No hay ediciones a\u00FAn" }))] })] }) }));
};
// ============================================
// PROGRESS BAR
// ============================================
const ProgressBar = ({ progress, statusMessage, isVisible, }) => {
    if (!isVisible)
        return null;
    return (_jsxs(motion.div, { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, className: "px-6 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700", children: [_jsxs("div", { className: "flex items-center justify-between mb-1.5", children: [_jsx("p", { className: "text-sm text-slate-600 dark:text-slate-400", children: statusMessage || 'Analizando contenido...' }), _jsxs("span", { className: "text-xs text-slate-500 dark:text-slate-500 tabular-nums", children: [Math.round(progress), "%"] })] }), _jsx("div", { className: "h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden", children: _jsx(motion.div, { className: "h-full bg-gradient-to-r from-primary-500 to-amber-500 rounded-full", initial: { width: 0 }, animate: { width: `${progress}%` }, transition: { duration: 0.3, ease: 'easeOut' } }) })] }));
};
// ============================================
// MAIN COMPONENT
// ============================================
export function AICanvasEditor({ lines, onLinesChange, currentLineIndex, editHistory = [], statusMessage, isAnalyzing, showHistory = true, progress = 0, className = '', }) {
    const contentRef = useRef(null);
    // Auto-scroll to current line
    useEffect(() => {
        if (currentLineIndex >= 0 && contentRef.current) {
            const lineElement = contentRef.current.children[currentLineIndex];
            if (lineElement) {
                lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [currentLineIndex]);
    return (_jsxs("div", { className: `flex h-full bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 ${className}`, children: [_jsx(AnimatePresence, { children: showHistory && isAnalyzing && (_jsx(EditHistorySidebar, { history: editHistory, isVisible: true })) }), _jsxs("div", { className: "flex-1 flex flex-col overflow-hidden", children: [_jsx(AnimatePresence, { children: isAnalyzing && (_jsx(ProgressBar, { progress: progress, statusMessage: statusMessage, isVisible: true })) }), _jsx("div", { ref: contentRef, className: "flex-1 overflow-y-auto px-10 py-8", children: _jsx(LayoutGroup, { children: _jsx("div", { className: "max-w-3xl mx-auto space-y-3", children: _jsx(AnimatePresence, { mode: "popLayout", children: lines.map((line, index) => (_jsx(AnimatedLine, { line: line, isActive: index === currentLineIndex }, line.id))) }) }) }) }), _jsx(AnimatePresence, { children: !isAnalyzing && progress >= 100 && (_jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, className: "absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm", children: _jsxs(motion.div, { initial: { scale: 0.9, y: 20 }, animate: { scale: 1, y: 0 }, className: "bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-6 text-center", children: [_jsx(motion.span, { className: "text-4xl block mb-2", animate: { scale: [1, 1.2, 1] }, transition: { duration: 0.5, delay: 0.2 }, children: "\u2728" }), _jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "An\u00E1lisis completado" }), _jsxs("p", { className: "text-sm text-slate-500 dark:text-slate-400 mt-1", children: [editHistory.length, " cambios aplicados"] })] }) })) })] })] }));
}
// ============================================
// UTILITY: Convert markdown to ContentLines
// ============================================
export function markdownToContentLines(markdown) {
    const lines = markdown.split('\n');
    const contentLines = [];
    lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (!trimmed)
            return; // Skip empty lines
        let contentLine = {
            id: `line-${index}`,
            content: trimmed,
            type: 'paragraph',
            status: 'idle',
        };
        // Detect headings
        if (trimmed.startsWith('###')) {
            contentLine.type = 'heading';
            contentLine.level = 3;
            contentLine.content = trimmed.replace(/^###\s*/, '');
        }
        else if (trimmed.startsWith('##')) {
            contentLine.type = 'heading';
            contentLine.level = 2;
            contentLine.content = trimmed.replace(/^##\s*/, '');
        }
        else if (trimmed.startsWith('#')) {
            contentLine.type = 'heading';
            contentLine.level = 1;
            contentLine.content = trimmed.replace(/^#\s*/, '');
        }
        // Detect list items  
        else if (trimmed.startsWith('-') || trimmed.startsWith('*') || /^\d+\./.test(trimmed)) {
            contentLine.type = 'list-item';
            contentLine.content = trimmed.replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, '');
        }
        // Detect quotes
        else if (trimmed.startsWith('>')) {
            contentLine.type = 'quote';
            contentLine.content = trimmed.replace(/^>\s*/, '');
        }
        // Detect dividers
        else if (/^[-_*]{3,}$/.test(trimmed)) {
            contentLine.type = 'divider';
            contentLine.content = '';
        }
        contentLines.push(contentLine);
    });
    return contentLines;
}
export default AICanvasEditor;
